
import { ComponentExample, ComponentType, ComplexityLevel, FibonacciEstimate } from "../types/estimation-types";

export const componentTypes: ComponentType[] = [
  'Pages/Screens',
  'Workflow',
  'API',
  'Integration',
  'Rule Engine',
  'DB Objects',
  'Reports/Dashboards'
];

export const complexityLevels: ComplexityLevel[] = [
  'Simple',
  'Medium',
  'Complex',
  'Supercomplex'
];

export const fibonacciEstimates: FibonacciEstimate[] = [
  {
    points: 1,
    manDays: 0.5,
    description: "Trivial change, typically less than half a day's work"
  },
  {
    points: 2,
    manDays: 1,
    description: "Simple task, typically 1 day's work"
  },
  {
    points: 3,
    manDays: 2,
    description: "Small feature, typically 2 days' work"
  },
  {
    points: 5,
    manDays: 3,
    description: "Medium complexity feature, typically 3 days' work"
  },
  {
    points: 8,
    manDays: 5,
    description: "Complex feature, typically 5 days' work"
  },
  {
    points: 13,
    manDays: 8,
    description: "Very complex feature, typically 8 days' work"
  },
  {
    points: 21,
    manDays: 11,
    description: "Extremely complex feature, typically 11 days' work"
  }
];

export const estimationResources = [
  {
    title: "Mike Cohn's Guide to Story Points",
    url: "https://www.mountaingoatsoftware.com/blog/what-are-story-points",
    description: "Detailed explanation of story points estimation from the creator of the concept."
  },
  {
    title: "Atlassian: Estimation Techniques",
    url: "https://www.atlassian.com/agile/project-management/estimation",
    description: "Comprehensive guide to agile estimation techniques."
  }
];

export const componentExamples: ComponentExample[] = [
  // Pages/Screens examples
  {
    componentType: 'Pages/Screens',
    complexity: 'Simple',
    description: 'Single entity, basic layout',
    example: 'Provider Info page with just profile details'
  },
  {
    componentType: 'Pages/Screens',
    complexity: 'Medium',
    description: 'Parent-child layout, 2–3 entities',
    example: 'Youth and Household form with relationship records'
  },
  {
    componentType: 'Pages/Screens',
    complexity: 'Complex',
    description: 'Tabs, conditional sections, ADA compliance',
    example: 'Service Plan page with dynamic visibility based on user roles'
  },
  {
    componentType: 'Pages/Screens',
    complexity: 'Supercomplex',
    description: 'Modals, nested relationships, AI autofill',
    example: 'Risk Score dashboard with comprehensive child welfare data visualization'
  },
  
  // Workflow examples
  {
    componentType: 'Workflow',
    complexity: 'Simple',
    description: 'Straight path flow',
    example: 'Case Intake → Initial Assessment assignment'
  },
  {
    componentType: 'Workflow',
    complexity: 'Medium',
    description: 'Conditional routing',
    example: 'Foster care placement approval with supervisor review'
  },
  {
    componentType: 'Workflow',
    complexity: 'Complex',
    description: 'Multi-stage with retry, escalation',
    example: 'Safety Plan escalation with alerts to caseworker, supervisor, and administrator'
  },
  {
    componentType: 'Workflow',
    complexity: 'Supercomplex',
    description: 'Dynamic routing, loops, AI triggers',
    example: 'Auto-close child support cases with LLM validation of payment history and compliance'
  },
  
  // API examples
  {
    componentType: 'API',
    complexity: 'Simple',
    description: 'Single GET/POST API',
    example: 'Fetch Family Service Provider information'
  },
  {
    componentType: 'API',
    complexity: 'Medium',
    description: '2-3 CRUD APIs',
    example: 'Create/Update Child Record, Update Case Status'
  },
  {
    componentType: 'API',
    complexity: 'Complex',
    description: 'APIs with security headers, rate limiting',
    example: 'Court document submission with PII protection and validation'
  },
  {
    componentType: 'API',
    complexity: 'Supercomplex',
    description: 'High-performance, secured APIs with caching',
    example: 'Statewide child registry search with complex matching algorithms'
  },
  
  // Integration examples
  {
    componentType: 'Integration',
    complexity: 'Simple',
    description: 'Pull data from single source',
    example: 'Foster Parent Directory integration'
  },
  {
    componentType: 'Integration',
    complexity: 'Medium',
    description: 'Merge/join data from 2-3 internal systems',
    example: 'Juvenile Justice and Child Welfare case history consolidation'
  },
  {
    componentType: 'Integration',
    complexity: 'Complex',
    description: 'Cross-platform joins, transformation logic',
    example: 'Syncing court dates and case events with county court management systems'
  },
  {
    componentType: 'Integration',
    complexity: 'Supercomplex',
    description: 'Real-time sync, AI-triggered workflows',
    example: 'Multi-county abuse registry synchronization with automated alerts'
  },
  
  // Reports/Dashboards examples
  {
    componentType: 'Reports/Dashboards',
    complexity: 'Simple',
    description: 'Flat report from one table',
    example: 'Open Case List by Caseworker'
  },
  {
    componentType: 'Reports/Dashboards',
    complexity: 'Medium',
    description: 'Filters and drilldown',
    example: 'Monthly child placement statistics by county and age'
  },
  {
    componentType: 'Reports/Dashboards',
    complexity: 'Complex',
    description: 'Cross-source, calculated fields',
    example: 'Child support collection rate per county with compliance metrics'
  },
  {
    componentType: 'Reports/Dashboards',
    complexity: 'Supercomplex',
    description: 'Predictive insights or LLM summaries',
    example: 'AI-generated placement risk assessment with outcome predictions'
  },
  
  // DB Objects examples
  {
    componentType: 'DB Objects',
    complexity: 'Simple',
    description: 'Simple SELECT/UPDATE trigger',
    example: 'Update case status when placement changes'
  },
  {
    componentType: 'DB Objects',
    complexity: 'Medium',
    description: 'Functions with input validation',
    example: 'Address validation and geocoding for service provider locations'
  },
  {
    componentType: 'DB Objects',
    complexity: 'Complex',
    description: 'Stored proc with joins, conditional logic',
    example: 'Case relationship builder with kinship network mapping'
  },
  {
    componentType: 'DB Objects',
    complexity: 'Supercomplex',
    description: 'Multi-step SPs with transactions',
    example: 'Family reunification eligibility calculation with multi-system data'
  },
  
  // Rule Engine examples
  {
    componentType: 'Rule Engine',
    complexity: 'Simple',
    description: 'Basic if/then rules',
    example: 'Eligibility determination based on age and income thresholds'
  },
  {
    componentType: 'Rule Engine',
    complexity: 'Medium',
    description: 'Multiple condition rules with AND/OR logic',
    example: 'Service package determination with dynamic program requirements'
  },
  {
    componentType: 'Rule Engine',
    complexity: 'Complex',
    description: 'Decision trees with validation and dependencies',
    example: 'Risk assessment scoring with multiple weighted factors'
  },
  {
    componentType: 'Rule Engine',
    complexity: 'Supercomplex',
    description: 'AI-assisted rules with machine learning components',
    example: 'Adaptive fraud detection system with learning capabilities'
  }
];

export const pointValues: Record<ComponentType, Record<ComplexityLevel, number>> = {
  'Pages/Screens': {
    'Simple': 1,
    'Medium': 3,
    'Complex': 5,
    'Supercomplex': 8
  },
  'Workflow': {
    'Simple': 3,
    'Medium': 5,
    'Complex': 13,
    'Supercomplex': 21
  },
  'API': {
    'Simple': 3,
    'Medium': 5,
    'Complex': 8,
    'Supercomplex': 13
  },
  'Integration': {
    'Simple': 3,
    'Medium': 5,
    'Complex': 13,
    'Supercomplex': 21
  },
  'Reports/Dashboards': {
    'Simple': 1,
    'Medium': 3,
    'Complex': 5,
    'Supercomplex': 8
  },
  'DB Objects': {
    'Simple': 2,
    'Medium': 5,
    'Complex': 8,
    'Supercomplex': 13
  },
  'Rule Engine': {
    'Simple': 2,
    'Medium': 5,
    'Complex': 8,
    'Supercomplex': 13
  }
};

export const getExamplesForType = (componentType: ComponentType): ComponentExample[] => {
  return componentExamples.filter(example => example.componentType === componentType);
};

export const getEstimatedPoints = (componentType: ComponentType, complexity: ComplexityLevel): number => {
  return pointValues[componentType][complexity];
};

export const getManDaysFromPoints = (points: number): number => {
  // Ensure exact conversion based on the specified scale
  switch (points) {
    case 1: return 0.5;
    case 2: return 1;
    case 3: return 2;
    case 5: return 3;
    case 8: return 5;
    case 13: return 8;
    case 21: return 11;
    default: 
      // Find closest match if not a standard value
      const estimate = fibonacciEstimates.find(est => est.points === points);
      return estimate ? estimate.manDays : points * 0.5; // Fallback
  }
};

export const getComponentTooltip = (componentType: ComponentType): string => {
  const points = Object.entries(pointValues[componentType])
    .map(([key, value]) => `${key}: ${value} points`)
    .join(', ');
  
  const description = {
    'Pages/Screens': 'User interface elements for displaying and capturing data',
    'Workflow': 'Process flows and state transitions',
    'API': 'Backend services for data access and operations',
    'Integration': 'Connections to external systems and data sources',
    'Rule Engine': 'Business rules and decision logic',
    'DB Objects': 'Database procedures, functions, and triggers',
    'Reports/Dashboards': 'Data visualizations and analytics'
  }[componentType];
  
  return `${description}. Points: ${points}`;
};
